import {
  COLLECTIONS,
  type ChangeRequestDoc,
  type ContractorDoc,
  type JobDoc,
  type NotificationDoc,
  type RegistrationDoc,
} from '@crew/shared';
import { collection, doc, getDoc, limit, onSnapshot, orderBy, query, where, type DocumentData, type FirestoreError } from 'firebase/firestore';
import { useEffect, useState } from 'react';
import { useAdmin } from '@/auth/AuthProvider';
import { demoStore } from '@/demo/demoStore';
import { IS_DEMO } from '@/demo/demoMode';
import type { Ts } from '@/lib/ts';
import { readerUid } from '@/services/adminApi';
import { getFirebase } from '@/services/firebase';
import { firestoreErrorMessage, type JobView, type LiveQuery, type RegistrationView } from './useActiveJobs';

/** נתונים חיים לבקשות, למאגר ולהתראות. מנהלים קוראים מ-Firestore; כתיבה רק דרך השרת. */

export type RequestView = ChangeRequestDoc<Ts> & { id: string };
export type ContractorView = ContractorDoc<Ts> & { id: string };
export type NotificationView = NotificationDoc<Ts> & { id: string };

const withId = <T,>(d: { id: string; data(): DocumentData }) => ({ id: d.id, ...(d.data() as T) });

function useLive<T>(key: string, demo: () => T, firebase: (ok: (d: T) => void, err: (e: FirestoreError) => void) => () => void): LiveQuery<T> {
  const [result, setResult] = useState<LiveQuery<T>>({ state: 'loading' });
  useEffect(() => {
    setResult({ state: 'loading' });
    if (IS_DEMO) {
      const push = () => setResult({ state: 'ready', data: demo() });
      push();
      return demoStore.subscribe(push);
    }
    return firebase(
      (data) => setResult({ state: 'ready', data }),
      (e) => setResult({ state: 'error', message: firestoreErrorMessage(e.code) }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return result;
}

/** בקשות ממתינות מכל העבודות, מהחדשה לישנה. */
export function usePendingRequests(): LiveQuery<RequestView[]> {
  return useLive(
    'requests:pending',
    () => demoStore.requests().filter((r) => r.status === 'pending'),
    (ok, err) => {
      const { db } = getFirebase();
      const q = query(collection(db, COLLECTIONS.changeRequests), where('status', '==', 'pending'), orderBy('createdAt', 'desc'), limit(200));
      return onSnapshot(q, (s) => ok(s.docs.map((d) => withId<ChangeRequestDoc<Ts>>(d))), err);
    },
  );
}

/** היסטוריית בקשות (שטופלו). */
export function useRequestHistory(max = 100): LiveQuery<RequestView[]> {
  return useLive(
    `requests:history:${max}`,
    () => demoStore.requests().filter((r) => r.status !== 'pending'),
    (ok, err) => {
      const { db } = getFirebase();
      const q = query(collection(db, COLLECTIONS.changeRequests), orderBy('createdAt', 'desc'), limit(max));
      return onSnapshot(q, (s) => ok(s.docs.map((d) => withId<ChangeRequestDoc<Ts>>(d)).filter((r) => r.status !== 'pending')), err);
    },
  );
}

export function useJobPendingRequests(jobId: string): LiveQuery<RequestView[]> {
  return useLive(
    `requests:job:${jobId}`,
    () => demoStore.requests().filter((r) => r.jobId === jobId && r.status === 'pending'),
    (ok, err) => {
      const { db } = getFirebase();
      const q = query(collection(db, COLLECTIONS.changeRequests), where('jobId', '==', jobId), where('status', '==', 'pending'));
      return onSnapshot(q, (s) => ok(s.docs.map((d) => withId<ChangeRequestDoc<Ts>>(d))), err);
    },
  );
}

export function useContractors(max = 1000): LiveQuery<ContractorView[]> {
  return useLive(
    `contractors:${max}`,
    () => demoStore.contractors(),
    (ok, err) => {
      const { db } = getFirebase();
      const q = query(collection(db, COLLECTIONS.contractors), orderBy('lastSeenAt', 'desc'), limit(max));
      return onSnapshot(q, (s) => ok(s.docs.map((d) => withId<ContractorDoc<Ts>>(d))), err);
    },
  );
}

export function useContractor(id: string): LiveQuery<ContractorView | null> {
  return useLive(
    `contractor:${id}`,
    () => demoStore.contractor(id),
    (ok, err) => {
      const { db } = getFirebase();
      return onSnapshot(doc(db, COLLECTIONS.contractors, id), (s) => ok(s.exists() ? { id: s.id, ...(s.data() as ContractorDoc<Ts>) } : null), err);
    },
  );
}

export interface HistoryRow {
  job: JobView;
  reg: RegistrationView;
}

/** היסטוריית העבודות של קבלן: לכל עבודה — הכמות שלו בה. נטען פעם אחת (לא חי). */
export function useContractorHistory(c: ContractorView | null): LiveQuery<HistoryRow[]> {
  const [result, setResult] = useState<LiveQuery<HistoryRow[]>>({ state: 'loading' });
  const key = c ? `${c.id}:${c.jobIds.join(',')}` : '';
  useEffect(() => {
    if (!c) return;
    if (IS_DEMO) {
      const push = () => setResult({ state: 'ready', data: demoStore.contractorHistory(c.id) as unknown as HistoryRow[] });
      push();
      return demoStore.subscribe(push);
    }
    let cancelled = false;
    const { db } = getFirebase();
    Promise.all(
      c.jobIds.slice(-100).map(async (jobId) => {
        const [j, r] = await Promise.all([
          getDoc(doc(db, COLLECTIONS.jobs, jobId)),
          getDoc(doc(db, COLLECTIONS.jobs, jobId, COLLECTIONS.registrations, c.id)),
        ]);
        if (!j.exists() || !r.exists()) return null;
        return { job: { id: j.id, ...(j.data() as JobDoc<Ts>) }, reg: { id: r.id, ...(r.data() as RegistrationDoc<Ts>) } };
      }),
    )
      .then((rows) => {
        if (cancelled) return;
        const data = rows.filter((x): x is HistoryRow => x !== null).sort((a, b) => b.job.date.localeCompare(a.job.date));
        setResult({ state: 'ready', data });
      })
      .catch((e: FirestoreError) => !cancelled && setResult({ state: 'error', message: firestoreErrorMessage(e.code) }));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return result;
}

export function useNotifications(max = 100): LiveQuery<NotificationView[]> {
  const { user } = useAdmin();
  const uid = readerUid(user);
  const live = useLive(
    `notifications:${max}`,
    () => demoStore.notifications(),
    (ok, err) => {
      const { db } = getFirebase();
      const q = query(collection(db, COLLECTIONS.notifications), orderBy('createdAt', 'desc'), limit(max));
      return onSnapshot(q, (s) => ok(s.docs.map((d) => withId<NotificationDoc<Ts>>(d))), err);
    },
  );
  // מנהל לא מקבל התראות על פעולות של עצמו
  if (live.state !== 'ready') return live;
  return { state: 'ready', data: live.data.filter((n) => n.actorUid !== uid) };
}

export function useUnreadCount(): number {
  const { user } = useAdmin();
  const uid = readerUid(user);
  const n = useNotifications(100);
  return n.state === 'ready' ? n.data.filter((x) => !x.readBy?.[uid]).length : 0;
}
